import {Injectable} from '@angular/core';
import {WizardNestedFormService} from '../abstract/wizard-nested-form-service';
import {FormArray, FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {minLengthValidator} from '@helpers/validators/minLength';
import {WizardFormEventsService} from '../wizard-form-events.service';
import {debounceTime, takeUntil} from 'rxjs/operators';
import {WizardValidators} from '../../wizard-validators';
import {MethodsProvider} from '@providers/methods/methods.provider';
import {WizardInterfaces} from '../../../wizard.namespace';
import {ModelsInterfaces} from '@namespaces/models.namespace';
import Method = ModelsInterfaces.Method;

@Injectable()
export class AttestationService extends WizardNestedFormService {
    form = this.fb.group({
        _touched: [false, Validators.requiredTrue],

        /**
         * Список выбранных областей аттестации
         */
        list: this.fb.array([], minLengthValidator(1, 'Необходимо добавить хотя бы один метод контроля'))
    });

    constructor(
        private fb: FormBuilder,
        private wizardFormEvents: WizardFormEventsService,
        private methodsProvider: MethodsProvider,
    ) {
        super();
    }

    init() {
        this.form.get('list').valueChanges.pipe(
            takeUntil(this.destroyed$),
            debounceTime(30),
        ).subscribe((value) => {
            this.wizardFormEvents.setAttestation(value);

            this.wizardFormEvents.setIsRk(this.hasRk());
        });

        this.wizardFormEvents.isRk$.subscribe(isRk => {
            if (isRk) {
                this.addRk();
            } else {
                this.removeRk();
            }
        });
    }

    restoreValidators() {
        const list = this.form.get('list') as FormArray;
        list.controls.forEach((group: FormGroup) => {
            if (!group.contains('title')) {
                group.setControl('title', new FormControl(''));
            }

            if (!group.contains('key')) {
                group.setControl('key', new FormControl(''));
            }

            if (!group.contains('objects')) {
                group.setControl('objects', new FormArray([]));
            }

            if (!group.contains('submethods')) {
                group.setControl('submethods', new FormArray([]));
            }
            group.get('key').setValidators([WizardValidators.required('Метод контроля обязательно должен быть указан.', false)]);
            group.get('objects').setValidators([minLengthValidator(1, 'Выберите объекты контроля')]);
        });
    }

    /**
     * Добавить новый кортеж (метод + объекты) области аттестации.
     * При добавлении объекты будут скопированы с первой области аттестации
     */
    public add(method: ModelsInterfaces.Method = null) {
        const list = this.form.get('list') as FormArray;
        const newGroup = this.getNewAttestationGroup(method);

        /**
         * Если там уже есть область аттестации, то копируем объекты с самой первой
         */
        if (list.length >= 1) {
            const firstGroup = list.controls[0] as FormGroup;
            const objectsControl = newGroup.get('objects') as FormArray;
            (firstGroup.value as WizardInterfaces.AttestationValue).objects.forEach(obj => {
                objectsControl.push(this.fb.group(obj));
            });
        }
        list.push(newGroup);
    }

    private getNewAttestationGroup(method: Method = null) {
        return this.fb.group({
            key: [method ? method.short_title : '', WizardValidators.required('Метод контроля обязательно должен быть указан.', false)],
            objects: this.fb.array([], minLengthValidator(1, 'Выберите объекты контроля')),
            submethods: this.fb.array([]),
            title: [method ? method.title : '']
        });
    }

    /**
     * Выбран ли РК в текущей области аттестации
     */
    private hasRk() {
        const list = this.form.get('list') as FormArray;
        return list.controls.some((group: FormGroup) => {
            return group.get('key') && group.get('key').value === 'РК';
        });
    }

    /**
     * Добавляем Метод РК в область аттестации
     */
    private addRk() {
        if (this.hasRk()) {
            return;
        }
        this.add(this.methodsProvider.getByShortTitle('РК'));
        this.setTouched(false);
    }

    /**
     * Удаляем РК из области аттестации
     */
    private removeRk() {
        if (!this.hasRk()) {
            return;
        }
        const list = this.form.get('list') as FormArray;
        const listValue = list.value as WizardInterfaces.AttestationValue[];
        const rkValue = listValue.find(v => v.key === 'РК');
        const index = listValue.indexOf(rkValue);

        if (index >= 0 && index < listValue.length) {
            list.removeAt(index);
            this.setTouched(false);
        }
    }
}
